import { useEffect } from "react";
import { Delete } from "@mui/icons-material";
import {
  Alert,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import type { Paquete } from "../../firebase/firestore/paquetes";
import type { Usuario } from "../../firebase/firestore/usuarios";
import type Fee from "../../interfaces/Fee";
import {
  camposFaltantesSegunEstado,
  esEstadoEntregado,
  mensajeEntregaIncompleta,
} from "../../utils/entregaValidations";
import { useEstados } from "../../utils/estadosUtils";
import { useAgenciaStore } from "../../zustand/useAgenciaStore";

type FieldPath = keyof Paquete | "peso.monto" | "peso.unidad";

export type PaqueteForm = Omit<Partial<Paquete>, "cobroAdicional" | "peso"> & {
  cobroAdicional?: number;
  peso?: {
    monto?: number;
    unidad: "kg" | "lb";
  };
};

export type PaqueteFormValue =
  | string
  | number
  | undefined
  | Fee
  | { monto?: number; unidad: "kg" | "lb" }
  | null;

interface PaqueteItemProps {
  paquete: PaqueteForm;
  index: number;
  touched: Partial<Record<FieldPath, boolean>>;
  onChange: (
    index: number,
    field: keyof Paquete,
    value: PaqueteFormValue
  ) => void;
  onBlur: (index: number, field: FieldPath) => void;
  onRemove: (index: number) => void;
  disableRemove: boolean;
  usuario?: Usuario;
  edit?: boolean;
}

export default function PaqueteItem({
  paquete,
  index,
  touched,
  onChange,
  onBlur,
  onRemove,
  disableRemove,
  usuario,
}: PaqueteItemProps) {
  const { agencia } = useAgenciaStore();

  const esEntregado = esEstadoEntregado(agencia, paquete.estado);
  // Un paquete entregado necesita peso y tarifa para calcular el total.
  const faltantesEntrega = camposFaltantesSegunEstado(
    agencia,
    paquete.estado,
    paquete
  );
  const faltaPeso = faltantesEntrega.includes("peso");
  const faltaTarifa = faltantesEntrega.includes("tarifa");
  const tienePersonalizada = !!(usuario?.tarifaAerea || usuario?.tarifaMaritima);
  const pctPropio = agencia?.referidos?.propio ?? 5;
  const base = (paquete.peso?.monto ?? 0) * (paquete.tarifa?.monto ?? 0);
  const puntosCalculados = tienePersonalizada
    ? 0
    : Math.floor((base * pctPropio) / 100);

  // Rellena automáticamente los puntos acumulados (5% de peso × tarifa,
  // redondeado al inmediato inferior). El campo no es editable.
  useEffect(() => {
    if (!esEntregado) return;
    if ((paquete.puntosAcumulados ?? null) !== puntosCalculados) {
      onChange(index, "puntosAcumulados", puntosCalculados);
    }
  }, [esEntregado, puntosCalculados, paquete.puntosAcumulados, index, onChange]);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        padding: 2,
      }}
    >
      <Grid size={{ xs: 4 }}>
        <TextField
          fullWidth
          label="Id de rastreo"
          size="small"
          value={paquete.idRastreo}
          onBlur={() => onBlur(index, "idRastreo")}
          onChange={(e) => onChange(index, "idRastreo", e.target.value)}
          error={touched?.idRastreo && !paquete?.idRastreo?.trim()}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <FormControl fullWidth>
          <InputLabel id={`via-label-${index}`}>Vía</InputLabel>
          <Select
            size="small"
            value={paquete.via}
            label="Vía"
            labelId={`via-label-${index}`}
            onChange={(e) => onChange(index, "via", e.target.value)}
            onBlur={() => onBlur(index, "via")}
            error={touched?.via && !paquete?.via}
          >
            <MenuItem value="aereo">Aérea</MenuItem>
            <MenuItem value="maritimo">Marítima</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 4 }}>
        <FormControl fullWidth>
          <InputLabel id={`estado-label-${index}`}>Estado</InputLabel>
          <Select
            size="small"
            value={paquete.estado}
            label="Estado"
            labelId={`estado-label-${index}`}
            onChange={(e) => onChange(index, "estado", e.target.value)}
          >
            {useEstados().map((estado) => (
              <MenuItem key={estado.id} value={estado.id}>
                {estado.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <TextField
          fullWidth
          label="Contenido"
          size="small"
          value={paquete.contenido}
          onChange={(e) => onChange(index, "contenido", e.target.value)}
        />
      </Grid>
      <Grid size={{ xs: 6 }} sx={{ display: "flex", gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          label="Peso"
          type="number"
          value={paquete.peso?.monto ?? ""}
          onChange={(e) =>
            onChange(index, "peso", {
              monto:
                e.target.value === "" ? undefined : Number(e.target.value),
              unidad: paquete.peso?.unidad ?? "kg",
            })
          }
          error={faltaPeso}
          helperText={
            faltaPeso ? "Requerido para marcar el paquete como entregado" : undefined
          }
          inputProps={{ min: 0, step: 0.01 }}
        />
        <Select
          size="small"
          value={paquete.peso?.unidad ?? "kg"}
          onChange={(e) =>
            onChange(index, "peso", {
              monto: paquete.peso?.monto,
              unidad: e.target.value as "kg" | "lb",
            })
          }
          sx={{ minWidth: 80 }}
        >
          <MenuItem value="lb">lb</MenuItem>
          <MenuItem value="kg">kg</MenuItem>
        </Select>
      </Grid>

      <Grid size={{ xs: 6 }}>
        <FormControl
          fullWidth
          variant="outlined"
          size="small"
          error={faltaTarifa}
        >
          <InputLabel id={`tarifa-label-${index}`}>Tarifa</InputLabel>
          <Select
            size="small"
            value={paquete.tarifa ? JSON.stringify(paquete.tarifa) : ""}
            renderValue={(tarifaString) => {
              if (!tarifaString) return "Seleccionar tarifa";
              const tarifa = JSON.parse(tarifaString);
              return `${tarifa.monto} ${tarifa.moneda} - ${tarifa.nombre}`;
            }}
            label="Tarifa"
            labelId={`tarifa-label-${index}`}
            onChange={(e) =>
              onChange(
                index,
                "tarifa",
                e.target.value ? JSON.parse(e.target.value) : null
              )
            }
          >
            <MenuItem value="">
              <em>Seleccionar tarifa</em>
            </MenuItem>
            {agencia?.tarifas.map((tarifa) => (
              <MenuItem
                key={`${tarifa.nombre}-${tarifa.moneda}-${tarifa.monto}`}
                value={JSON.stringify(tarifa)}
              >
                {tarifa.nombre} - {tarifa.moneda} {tarifa.monto}
              </MenuItem>
            ))}
          </Select>
          {faltaTarifa && (
            <FormHelperText>
              Requerida para marcar el paquete como entregado
            </FormHelperText>
          )}
        </FormControl>
      </Grid>

      <Grid size={{ xs: 6 }}>
        <TextField
          fullWidth
          label="Cobro Adicional"
          size="small"
          type="number"
          value={paquete.cobroAdicional ?? ""}
          onChange={(e) =>
            onChange(
              index,
              "cobroAdicional",
              e.target.value === "" ? undefined : Number(e.target.value)
            )
          }
          inputProps={{ min: 0, step: 0.01 }}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          label="Ubicación física"
          size="small"
          value={paquete.ubicacionFisica ?? ""}
          onChange={(e) => onChange(index, "ubicacionFisica", e.target.value)}
          placeholder="Ej: Bodega A, Estante 3, Caja 12"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          label="Guía"
          size="small"
          value={paquete.envio ?? ""}
          onChange={(e) => onChange(index, "envio", e.target.value || null)}
          placeholder="Nombre de guía (opcional)"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          label="Observaciones"
          size="small"
          value={paquete.observaciones}
          onChange={(e) => onChange(index, "observaciones", e.target.value)}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <TextField
          fullWidth
          label="Costo"
          size="small"
          type="number"
          value={paquete.costo ?? ""}
          onChange={(e) =>
            onChange(index, "costo", e.target.value ? Number(e.target.value) : null)
          }
          inputProps={{ min: 0, step: 0.01 }}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <TextField
          fullWidth
          label="Ganancia"
          size="small"
          type="number"
          value={paquete.ganancia ?? ""}
          disabled
          inputProps={{ min: 0, step: 0.01 }}
          helperText="Calculado automáticamente"
        />
      </Grid>

      {esEntregado && (
        <>
          {faltantesEntrega.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="error">
                {mensajeEntregaIncompleta(faltantesEntrega)}.
              </Alert>
            </Grid>
          )}
          {usuario && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Puntos disponibles del usuario:</strong> {usuario.puntos ?? 0}
              </Alert>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Detalles de entrega"
              size="small"
              value={paquete.detallesEntrega ?? ""}
              onChange={(e) => onChange(index, "detallesEntrega", e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Puntos acumulados"
              size="small"
              type="number"
              value={puntosCalculados}
              disabled
              inputProps={{ min: 0, step: 1 }}
              helperText={
                tienePersonalizada
                  ? "Sin puntos (tarifa personalizada)"
                  : `${pctPropio}% de peso × tarifa`
              }
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Puntos redimidos"
              size="small"
              type="number"
              value={paquete.puntosRedimidos ?? ""}
              onChange={(e) =>
                onChange(index, "puntosRedimidos", e.target.value ? Number(e.target.value) : null)
              }
              inputProps={{ min: 0, step: 1 }}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Descuento"
              size="small"
              type="number"
              value={paquete.descuento ?? ""}
              onChange={(e) =>
                onChange(index, "descuento", e.target.value ? Number(e.target.value) : null)
              }
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
        </>
      )}

      {!paquete.id && (
        <Grid
          size={{ xs: 12 }}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <IconButton onClick={() => onRemove(index)} disabled={disableRemove}>
            <Delete />
          </IconButton>
        </Grid>
      )}
    </Grid>
  );
}
